/*
 * Copyright 2026 Sony Corporation
 */
import { z } from 'zod';

import {
  AcceptanceCriterionSchema,
  ChatEventSchema,
  ContractSchema,
  RunSchema,
  UiBriefSchema,
  type Contract,
} from './chat.js';

/**
 * A roadmap is how a request is built: in steps the user can see on the device one after another, each a version of
 * its own (docs/plan/milestone.md). It is proposed with the design options, approved once, then run step by step; the
 * user's messages revise the steps not started yet.
 */

/** `removed` steps stay for one revision so the panel can show what went away. */
export const RoadmapStepStateSchema = z.enum(['planned', 'running', 'done', 'failed', 'skipped', 'removed']);
export type RoadmapStepState = z.infer<typeof RoadmapStepStateSchema>;

/** Where a running step is: its code is written, built and reviewed, sent to the device, checked there. */
export const RoadmapStageSchema = z.enum(['writing', 'building', 'deploying', 'checking']);
export type RoadmapStage = z.infer<typeof RoadmapStageSchema>;

/** A step as the planner or the roadmap editor writes it. */
export const StepDraftSchema = z.object({
  step_id: z
    .string()
    .max(40)
    .optional()
    .describe('The id of an existing step this one keeps or changes; leave it out for a new step.'),
  title: z
    .string()
    .min(1)
    .max(60)
    .describe('What the user gets, in their language, a few words: "Live view with stop and resume".'),
  outcome: z
    .string()
    .min(1)
    .max(300)
    .describe('What the user can do or see on the device once the step is done, in one sentence, in their language.'),
  work: z
    .array(z.string().min(1).max(300))
    .min(1)
    .max(6)
    .describe('What is built in this step, in English, short: Go methods, page parts, tests.'),
  acceptance: z
    .array(AcceptanceCriterionSchema)
    .max(4)
    .default([])
    .describe('The criteria this step makes true (each criterion of the request belongs to exactly one step).'),
  go_api: z
    .string()
    .max(6000)
    .default('')
    .describe(
      'The Go declarations this step adds to the interface, exactly as they will be written (types with json tags, exported methods the page calls, unexported ones its tests call), with a one-line comment each. Empty when the step adds none. Never repeat what an earlier step declared.'
    ),
  element_ids: z
    .array(z.string().min(1).max(200))
    .max(30)
    .default([])
    .describe('Element ids this step adds to the page, as "id: what it is".'),
  probe: z
    .array(z.string().min(1).max(200))
    .max(10)
    .default([])
    .describe('Names this step exposes with probe.Expose, as "name: what it holds".'),
  page_parts: z
    .number()
    .int()
    .min(0)
    .max(10)
    .default(1)
    .describe('How many panels or groups of controls this step adds or changes on the page.'),
});
export type StepDraft = z.input<typeof StepDraftSchema>;

export const RoadmapStepSchema = StepDraftSchema.extend({
  step_id: z.string().min(1).max(40),
  /** Seconds the step is expected to take from its start to running on the device, from its size (estimateStep). */
  estimate_s: z.number().int().nonnegative(),
  /** Where it came from: the first breakdown, a message of the user, or a fix of what a step left wrong. */
  origin: z.enum(['roadmap', 'user', 'fix']).default('roadmap'),
  state: RoadmapStepStateSchema.default('planned'),
  stage: RoadmapStageSchema.nullable().default(null),
  run_id: z.string().nullable().default(null),
  version: z.number().int().positive().nullable().default(null),
  /** How the last revision touched it, shown until the next one; null when it did not. */
  change: z.enum(['added', 'changed', 'removed']).nullable().default(null),
  revised_in: z.number().int().positive().nullable().default(null),
  error: z.string().nullable().default(null),
  started_at: z.string().datetime().nullable().default(null),
  finished_at: z.string().datetime().nullable().default(null),
  /** When the device said this step's version runs. */
  applied_at: z.string().datetime().nullable().default(null),
  /** Numbers of the step's run: implement_ms, build_ms, deploy_ms, builds, output_tokens, ... */
  metrics: z.record(z.string(), z.number()).default({}),
});
export type RoadmapStep = z.infer<typeof RoadmapStepSchema>;

export const RoadmapStatusSchema = z.enum(['awaiting_approval', 'running', 'paused', 'done', 'cancelled']);
export type RoadmapStatus = z.infer<typeof RoadmapStatusSchema>;

export const MAX_STEPS = 10;

export const RoadmapSchema = z.object({
  project_id: z.string().uuid(),
  roadmap_id: z.string().min(1),
  /** The run that planned it; it becomes the run of the first step. */
  run_id: z.string().min(1),
  request: z.string(),
  /** The device every step's version is sent to. */
  device_id: z.string().uuid().nullable().default(null),
  /** The app once every step is done, in one or two sentences, in the user's language. */
  goal: z.string(),
  status: RoadmapStatusSchema,
  /** Raised by every change of the steps the user caused; the panel marks what the newest one changed. */
  revision: z.number().int().positive(),
  /** Raised by every write; a write only lands on the version it read. */
  etag: z.number().int().nonnegative().default(0),
  steps: z.array(RoadmapStepSchema).max(MAX_STEPS * 2),
  /** The steps before the newest revision, so it can be undone. */
  previous: z.array(RoadmapStepSchema).nullable().default(null),
  ui: UiBriefSchema.nullable().default(null),
  risks: z.array(z.string()).default([]),
  /** Why it is paused, when it is. */
  note: z.string().nullable().default(null),
  /** What the reviewer found in the last step's version when it was sent before it was fixed; the next step fixes it. */
  carry: z.string().nullable().default(null),
  approved_at: z.string().datetime().nullable().default(null),
  /** roadmap_ms (request to proposal), revisions, edit_ms (last message to revision), ... */
  metrics: z.record(z.string(), z.number()).default({}),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type Roadmap = z.infer<typeof RoadmapSchema>;

export const RoadmapActionRequestSchema = z
  .object({
    action: z.enum(['pause', 'resume', 'retry', 'skip']),
    step_id: z.string().min(1).max(40).optional(),
  })
  .strict();
export type RoadmapActionRequest = z.infer<typeof RoadmapActionRequestSchema>;

/** Steps that still count: the ones not taken out. */
export const liveSteps = (roadmap: Pick<Roadmap, 'steps'>): RoadmapStep[] =>
  roadmap.steps.filter((s) => s.state !== 'removed');

/** The next step to run: the first planned one, unless a step runs or failed before it. */
export const nextStep = (roadmap: Pick<Roadmap, 'steps'>): RoadmapStep | undefined => {
  for (const step of liveSteps(roadmap)) {
    if (step.state === 'planned') {
      return step;
    }
    if (step.state === 'running' || step.state === 'failed') {
      return undefined;
    }
  }
  return undefined;
};

export const runningStep = (roadmap: Pick<Roadmap, 'steps'>): RoadmapStep | undefined =>
  roadmap.steps.find((s) => s.state === 'running');

/** 1-based position among the steps that count. */
export const stepNumber = (roadmap: Pick<Roadmap, 'steps'>, stepId: string): number =>
  liveSteps(roadmap).findIndex((s) => s.step_id === stepId) + 1;

export const goMethods = (goApi: string): number => (goApi.match(/^\s*func\s*\(/gm) ?? []).length;

/**
 * Seconds a step is expected to take: a base for build, delivery and the check on the device, plus the writing, which
 * grows with the Go methods and the page parts (calibrated against gen-time.md r7-r10 and the roadmap benches).
 */
export const estimateStep = (step: Pick<StepDraft, 'go_api' | 'page_parts'>): number =>
  Math.round(75 + 15 * goMethods(step.go_api ?? '') + 25 * (step.page_parts ?? 1));

/** Seconds above which the planner is asked once whether the step splits into parts that each mean something. */
export const LONG_STEP_S = 180;

/**
 * The interface a step builds against: what the steps done so far declared plus its own. The steps after it are not
 * there yet, so their methods do not exist in its code.
 */
export const stepContract = (roadmap: Pick<Roadmap, 'steps'>, stepId: string): Contract | null => {
  const steps = liveSteps(roadmap);
  const index = steps.findIndex((s) => s.step_id === stepId);
  if (index < 0) {
    return null;
  }
  const upTo = steps.slice(0, index + 1).filter((s) => s.state === 'done' || s.step_id === stepId);
  const goApi = upTo
    .map((s) => s.go_api.trim())
    .filter(Boolean)
    .join('\n\n');
  if (!goApi) {
    return null;
  }
  return ContractSchema.parse({
    go_api: goApi.slice(0, 8000),
    element_ids: upTo.flatMap((s) => s.element_ids).slice(0, 60),
    probe: upTo.flatMap((s) => s.probe).slice(0, 20),
    parts: [],
  });
};

const CHECK_LABELS = { test: 'Go test', device: 'On the device', user: 'Your judgement' } as const;

const minutes = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `0:${String(s).padStart(2, '0')}`;
};

/** The roadmap as the chat shows it when it is proposed. */
export const formatRoadmap = (roadmap: Pick<Roadmap, 'goal' | 'steps' | 'risks'>): string => {
  const steps = liveSteps(roadmap);
  const total = steps.reduce((sum, s) => sum + s.estimate_s, 0);
  return [
    roadmap.goal,
    '',
    `| # | Step | What you can do then | About |`,
    `| --- | --- | --- | --- |`,
    ...steps.map((s, i) => `| ${i + 1} | ${s.title} | ${s.outcome.replace(/\|/g, '/')} | ${minutes(s.estimate_s)} |`),
    '',
    `Each step is built, checked and sent to the device as its own version; about ${minutes(total)} in all.`,
    ...(roadmap.risks.length > 0 ? ['', 'Watch out for:', ...roadmap.risks.map((r) => `- ${r}`)] : []),
  ].join('\n');
};

/** What changed between two lists of steps, for the chat. */
export const formatRevision = (roadmap: Pick<Roadmap, 'steps' | 'revision'>, summary: string): string => {
  const touched = roadmap.steps.filter((s) => s.revised_in === roadmap.revision && s.change);
  const sign = { added: '+', changed: '~', removed: '−' } as const;
  return [
    summary,
    '',
    ...touched.map(
      (s) => `- ${sign[s.change ?? 'changed']} ${s.title}${s.change === 'removed' ? '' : `: ${s.outcome}`}`
    ),
  ].join('\n');
};

/**
 * What the lead of a step is given as its approved plan: the step, what came before it (to keep working) and what
 * comes after (to leave alone).
 */
export const formatStepPlan = (
  roadmap: Pick<Roadmap, 'goal' | 'steps'> & { carry?: string | null },
  stepId: string
): string => {
  const steps = liveSteps(roadmap);
  const index = steps.findIndex((s) => s.step_id === stepId);
  const step = steps[index];
  if (!step) {
    throw new Error(`step ${stepId} is not in the roadmap`);
  }
  const done = steps.slice(0, index).filter((s) => s.state === 'done');
  const later = steps.slice(index + 1).filter((s) => s.state === 'planned');
  return [
    `This is step ${index + 1} of ${steps.length} of a roadmap: the app is built in steps, each one a version the user sees on the device. Do this step only.`,
    `The app in the end: ${roadmap.goal}`,
    '',
    `## Step ${index + 1}: ${step.title}`,
    step.outcome,
    '',
    ...step.work.map((w, i) => `${i + 1}. ${w}`),
    ...(step.acceptance.length > 0
      ? ['', 'Done when:', ...step.acceptance.map((c) => `- ${CHECK_LABELS[c.check]}: ${c.text}`)]
      : []),
    ...(done.length > 0
      ? [
          '',
          'Already built in earlier steps (keep it working; in the check on the device, also check it briefly again):',
          ...done.map((s) => `- ${s.title}: ${s.outcome}`),
        ]
      : []),
    ...(later.length > 0
      ? [
          '',
          'Later steps (do NOT build them now; leave their place on the page empty, without sample data or controls that do nothing, and never mention them in the app):',
          ...later.map((s) => `- ${s.title}: ${s.outcome}`),
        ]
      : []),
    ...(roadmap.carry
      ? [
          '',
          'Also fix what the reviewer found in the version of the step before, which was sent to the device before it was fixed:',
          roadmap.carry,
        ]
      : []),
    '',
    'Keep the final report to a few lines: what the user can do now and how the check on the device went.',
  ].join('\n');
};

const same = (a: StepDraft, b: RoadmapStep) =>
  a.title === b.title &&
  a.outcome === b.outcome &&
  JSON.stringify(a.work) === JSON.stringify(b.work) &&
  (a.go_api ?? '') === b.go_api &&
  JSON.stringify(a.acceptance ?? []) === JSON.stringify(b.acceptance);

let counter = 0;
const newStepId = (taken: Set<string>): string => {
  for (;;) {
    counter += 1;
    const id = `s${Date.now().toString(36).slice(-4)}${counter.toString(36)}`;
    if (!taken.has(id)) {
      return id;
    }
  }
};

/** A step as stored, from a draft. */
export const toStep = (draft: StepDraft, stepId: string, origin: RoadmapStep['origin']): RoadmapStep =>
  RoadmapStepSchema.parse({
    ...StepDraftSchema.parse(draft),
    step_id: stepId,
    estimate_s: estimateStep(draft),
    origin,
  });

/** The first steps of a new roadmap. */
export const initialSteps = (drafts: StepDraft[]): RoadmapStep[] =>
  drafts.map((d, i) => toStep({ ...d, step_id: undefined }, `s${i + 1}`, 'roadmap'));

/**
 * Replaces the steps not started yet with `drafts` and marks what that changed. Steps that ran (or run) stay as they
 * are, in front; a draft naming one of them is taken as a new step after them, since a version on the device is not
 * rewritten. Steps removed earlier are dropped now.
 */
export const reviseSteps = (roadmap: Roadmap, drafts: StepDraft[], now: string): Roadmap => {
  const revision = roadmap.revision + 1;
  const fixed = roadmap.steps.filter((s) => s.state !== 'planned' && s.state !== 'removed');
  const planned = new Map(roadmap.steps.filter((s) => s.state === 'planned').map((s) => [s.step_id, s]));
  const taken = new Set(roadmap.steps.map((s) => s.step_id));
  const kept = new Set<string>();
  const clean = (s: RoadmapStep): RoadmapStep => ({ ...s, change: null, revised_in: s.revised_in });
  const next: RoadmapStep[] = drafts.map((draft) => {
    const old = draft.step_id ? planned.get(draft.step_id) : undefined;
    if (old && !kept.has(old.step_id)) {
      kept.add(old.step_id);
      if (same(draft, old)) {
        return clean(old);
      }
      return {
        ...toStep(draft, old.step_id, old.origin),
        change: 'changed',
        revised_in: revision,
      };
    }
    const id = newStepId(taken);
    taken.add(id);
    return { ...toStep(draft, id, 'user'), change: 'added', revised_in: revision };
  });
  const removed = [...planned.values()]
    .filter((s) => !kept.has(s.step_id))
    .map((s) => ({ ...s, state: 'removed' as const, change: 'removed' as const, revised_in: revision }));
  return {
    ...roadmap,
    revision,
    previous: roadmap.steps,
    steps: [...fixed.map(clean), ...next, ...removed].slice(0, MAX_STEPS * 2),
    updated_at: now,
  };
};

/** The steps before the last revision, back, where they have not started since. */
export const undoRevision = (roadmap: Roadmap, now: string): Roadmap | undefined => {
  if (!roadmap.previous) {
    return undefined;
  }
  const current = new Map(roadmap.steps.map((s) => [s.step_id, s]));
  const steps = roadmap.previous
    .filter((s) => s.state !== 'removed')
    .map((s) => {
      const since = current.get(s.step_id);
      // What ran since stays as it ran.
      return since && since.state !== 'planned' && since.state !== 'removed' ? since : { ...s, change: null };
    });
  for (const s of roadmap.steps) {
    if (s.state !== 'planned' && s.state !== 'removed' && !steps.some((t) => t.step_id === s.step_id)) {
      steps.push(s);
    }
  }
  return { ...roadmap, steps, previous: null, revision: roadmap.revision + 1, updated_at: now };
};

/** How many steps are settled (done or skipped) of those that count. */
export const settledSteps = (roadmap: Pick<Roadmap, 'steps'>): number =>
  liveSteps(roadmap).filter((s) => s.state === 'done' || s.state === 'skipped').length;

export const ListChatResponseSchema = z.object({
  events: z.array(ChatEventSchema),
  /** The newest run, which is the one that may need an answer. */
  run: RunSchema.nullable(),
  /** The project's newest roadmap; null before the first one. */
  roadmap: RoadmapSchema.nullable().default(null),
});
export type ListChatResponse = z.infer<typeof ListChatResponseSchema>;
